// src/pages/buyer/CartPage.jsx
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useCart } from '@/context/CartContext'
import { useCurrency } from '@/components/common/CurrencySwitcher'
import { cartApi, loyaltyApi, giftCardsApi } from '@/api/services'
import { Button, Card, CardHeader, CardBody } from '@/components/common/ui'
import { Trash2, Plus, Minus, Tag, Star, Gift, ShoppingBag, AlertTriangle, TrendingDown } from 'lucide-react'
import toast from 'react-hot-toast'

export default function CartPage() {
  const { items, subtotal, updateItem, removeItem, clearCart } = useCart()
  const { format } = useCurrency()
  const navigate = useNavigate()

  const [couponCode, setCouponCode]   = useState('')
  const [couponData, setCouponData]   = useState(null)
  const [loyaltyPoints, setLoyaltyPoints] = useState(0)
  const [loyaltyDiscount, setLoyaltyDiscount] = useState(0)
  const [gcCode, setGcCode]           = useState('')
  const [gcData, setGcData]           = useState(null)
  const [applying, setApplying]       = useState('')

  const applyCoupon = async () => {
    setApplying('coupon')
    try {
      const { data } = await cartApi.applyCoupon({ code: couponCode })
      setCouponData(data)
      toast.success(`Coupon applied! Saving ${format(data.discount_amount)}`)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid coupon')
    } finally { setApplying('') }
  }

  const applyLoyalty = async () => {
    if (!loyaltyPoints) return
    setApplying('loyalty')
    try {
      const { data } = await loyaltyApi.check(loyaltyPoints).then(r => r.data)
      setLoyaltyDiscount(data.discount_amount)
      toast.success(`${loyaltyPoints} points = ${format(data.discount_amount)} discount`)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Cannot apply points')
    } finally { setApplying('') }
  }

  const applyGiftCard = async () => {
    setApplying('gc')
    try {
      const { data } = await giftCardsApi.validate({ code: gcCode })
      setGcData(data.data)
      toast.success(`Gift card applied! Balance: ${format(data.data.balance)}`)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid gift card')
    } finally { setApplying('') }
  }

  const couponDiscount  = couponData?.discount_amount || 0
  const gcDiscount      = gcData ? Math.min(gcData.balance, subtotal) : 0
  const shipping        = subtotal >= 2000 ? 0 : 99
  const total           = Math.max(0, subtotal - couponDiscount - loyaltyDiscount - gcDiscount + shipping)

  if (!items.length) return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="text-6xl mb-5">🛒</div>
      <h2 className="font-display font-bold text-2xl mb-2">Your cart is empty</h2>
      <p className="text-muted text-sm mb-8">Add some beautiful fabrics to get started</p>
      <Link to="/fabrics" className="btn-primary px-8 py-3">Browse Fabrics</Link>
    </div>
  )

  return (
    <div className="max-w-5xl mx-auto">
      <h1 className="font-display font-bold text-2xl mb-6">Your Cart ({items.length})</h1>

      {/* Mixed cart warning */}
      {(() => {
        const hasWholesale = items.some(i => i.product?.is_wholesale_only)
        const hasRetail = items.some(i => !i.product?.is_wholesale_only)
        if (hasWholesale && hasRetail) return (
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
            <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-amber-800">Mixed Cart Warning</p>
              <p className="text-xs text-amber-700 mt-0.5">Your cart has both retail and wholesale-only items. Wholesale items may have different shipping timelines and MOQs. Consider placing separate orders for faster delivery.</p>
            </div>
          </div>
        )
        return null
      })()}

      <div className="grid lg:grid-cols-3 gap-6">

        {/* Cart items */}
        <div className="lg:col-span-2 space-y-3">
          {items.map(item => (
            <Card key={item.id} className="flex gap-4 p-4">
              <Link to={`/fabrics/${item.product?.slug}`} className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-surface">
                {item.product?.primary_image
                  ? <img src={item.product.primary_image} alt={item.product.name} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-2xl">🧵</div>
                }
              </Link>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Link to={`/fabrics/${item.product?.slug}`} className="font-bold text-sm hover:text-pink line-clamp-2">
                    {item.product?.name || item.product_name}
                  </Link>
                  {item.is_bulk_price && (
                    <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-green-50 text-green-700 text-[10px] font-bold rounded-full whitespace-nowrap">
                      <TrendingDown size={10} /> Bulk Price
                    </span>
                  )}
                  {item.product?.is_wholesale_only && (
                    <span className="px-1.5 py-0.5 bg-purple-50 text-purple-700 text-[10px] font-bold rounded-full whitespace-nowrap">Wholesale</span>
                  )}
                </div>
                <p className="text-xs text-muted mt-0.5">{item.product?.supplier?.business_name}</p>
                {item.is_bulk_price && item.retail_price && (
                  <p className="text-[11px] text-green-600 mt-0.5">
                    {format(item.price)}/{item.product?.unit || 'm'} <span className="line-through text-muted">{format(item.retail_price)}</span>
                    {' '}— saving {Math.round((1 - item.price / item.retail_price) * 100)}%
                  </p>
                )}
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => item.quantity > 1 && updateItem(item.id, item.quantity - 1)}
                      className="w-7 h-7 rounded-full border border-border flex items-center justify-center hover:border-pink hover:text-pink transition-colors">
                      <Minus size={12} />
                    </button>
                    <span className="text-sm font-bold w-8 text-center">{item.quantity}</span>
                    <button onClick={() => updateItem(item.id, item.quantity + 1)}
                      className="w-7 h-7 rounded-full border border-border flex items-center justify-center hover:border-pink hover:text-pink transition-colors">
                      <Plus size={12} />
                    </button>
                    <span className="text-xs text-muted ml-1">{item.product?.unit || 'm'}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-extrabold">{format(item.quantity * item.price)}</span>
                    <button onClick={() => removeItem(item.id)} className="text-muted hover:text-red-500 transition-colors">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Order summary */}
        <div className="space-y-4">
          <Card>
            <CardHeader><h2 className="font-bold text-base">Order Summary</h2></CardHeader>
            <CardBody className="space-y-3">
              <div className="flex justify-between text-sm"><span className="text-muted">Subtotal</span><span>{format(subtotal)}</span></div>
              {couponDiscount > 0 && <div className="flex justify-between text-sm text-green-700"><span>Coupon ({couponData?.code})</span><span>-{format(couponDiscount)}</span></div>}
              {loyaltyDiscount > 0 && <div className="flex justify-between text-sm text-green-700"><span>Loyalty Points</span><span>-{format(loyaltyDiscount)}</span></div>}
              {gcDiscount > 0 && <div className="flex justify-between text-sm text-green-700"><span>Gift Card</span><span>-{format(gcDiscount)}</span></div>}
              <div className="flex justify-between text-sm"><span className="text-muted">Shipping</span><span>{shipping === 0 ? <span className="text-green-700 font-bold">Free</span> : format(shipping)}</span></div>
              {shipping > 0 && <p className="text-xs text-muted">Free shipping on orders over {format(2000)}</p>}
              <div className="border-t border-border pt-3 flex justify-between font-extrabold text-base">
                <span>Total</span><span>{format(total)}</span>
              </div>
            </CardBody>
          </Card>

          {/* Coupon */}
          <Card>
            <CardBody className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-bold"><Tag size={14} className="text-pink" /> Coupon Code</div>
              <div className="flex gap-2">
                <input value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="WELCOME10" className="input flex-1 text-xs uppercase" />
                <Button variant="secondary" size="sm" loading={applying === 'coupon'} onClick={applyCoupon}>Apply</Button>
              </div>
            </CardBody>
          </Card>

          {/* Loyalty */}
          <Card>
            <CardBody className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-bold"><Star size={14} className="text-pink" /> Use Loyalty Points</div>
              <div className="flex gap-2">
                <input type="number" value={loyaltyPoints} onChange={e => setLoyaltyPoints(+e.target.value)}
                  placeholder="Points to use" className="input flex-1 text-xs" min="0" />
                <Button variant="secondary" size="sm" loading={applying === 'loyalty'} onClick={applyLoyalty}>Apply</Button>
              </div>
            </CardBody>
          </Card>

          {/* Gift card */}
          <Card>
            <CardBody className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-bold"><Gift size={14} className="text-pink" /> Gift Card</div>
              <div className="flex gap-2">
                <input value={gcCode} onChange={e => setGcCode(e.target.value.toUpperCase())}
                  placeholder="FAB-XXXX-XXXX" className="input flex-1 text-xs uppercase" />
                <Button variant="secondary" size="sm" loading={applying === 'gc'} onClick={applyGiftCard}>Apply</Button>
              </div>
            </CardBody>
          </Card>

          <Button className="w-full justify-center py-3.5 text-base"
            onClick={() => navigate('/checkout', { state: { couponData, loyaltyPoints, loyaltyDiscount, gcData, gcDiscount, total, shipping } })}>
            <ShoppingBag size={18} /> Proceed to Checkout
          </Button>
        </div>
      </div>
    </div>
  )
}
